import {
  $generateJSONFromSelectedNodes,
  $getClipboardDataFromSelection,
  setLexicalClipboardDataTransfer,
} from '@lexical/clipboard';
import { $convertToMarkdownString, type Transformer } from '@lexical/markdown';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $isTableCellNode, $isTableSelection } from '@lexical/table';
import {
  $createParagraphNode,
  $getSelection,
  $isRangeSelection,
  type BaseSelection,
  type LexicalEditor,
  type RangeSelection,
  COMMAND_PRIORITY_CRITICAL,
  COPY_COMMAND,
  CUT_COMMAND,
} from 'lexical';
import { useEffect, useMemo } from 'react';
import { createCopyTransformers } from '../config/transformers';
import { $spansMultipleBlocks } from '../utils/spansMultipleBlocks';
import { $tableSelectionToMarkdown } from '../utils/tableSelectionToMarkdown';

export default function CopyFormattingPlugin(): null {
  const [editor] = useLexicalComposerContext();
  const copyTransformers = useMemo(() => createCopyTransformers(editor), [editor]);

  useEffect(() => {
    const $writeClipboard = (selection: BaseSelection, clipboardData: DataTransfer) => {
      const data = $getClipboardDataFromSelection(selection);

      if ($isTableSelection(selection)) {
        const markdown = $tableSelectionToMarkdown(selection);
        if (markdown) {
          data['text/plain'] = markdown;
        }
      } else if ($isRangeSelection(selection) && $spansMultipleBlocks(selection)) {
        const markdown = $selectedRangeToMarkdown(editor, selection, copyTransformers);
        if (markdown) {
          data['text/plain'] = markdown;
        }
      }

      setLexicalClipboardDataTransfer(clipboardData, data);
    };

    const handleCopy = (event: ClipboardEvent) => {
      const selection = $getSelection();
      if (!selection || !event.clipboardData) return false;

      $writeClipboard(selection, event.clipboardData);
      event.preventDefault();
      return true;
    };

    const handleCut = (event: ClipboardEvent) => {
      const selection = $getSelection();
      if (!selection || !event.clipboardData) return false;

      $writeClipboard(selection, event.clipboardData);
      event.preventDefault();

      if ($isTableSelection(selection)) {
        for (const node of selection.getNodes()) {
          if ($isTableCellNode(node)) {
            node.getChildren().forEach(child => child.remove());
            node.append($createParagraphNode());
          }
        }
      } else if ($isRangeSelection(selection)) {
        selection.removeText();
      }

      return true;
    };

    const removeCopy = editor.registerCommand(COPY_COMMAND, handleCopy, COMMAND_PRIORITY_CRITICAL);

    const removeCut = editor.registerCommand(CUT_COMMAND, handleCut, COMMAND_PRIORITY_CRITICAL);

    return () => {
      removeCopy();
      removeCut();
    };
  }, [editor, copyTransformers]);

  return null;
}

function $selectedRangeToMarkdown(
  editor: LexicalEditor,
  selection: RangeSelection,
  transformers: Transformer[],
): string {
  const { nodes: serializedNodes } = $generateJSONFromSelectedNodes(editor, selection);
  if (serializedNodes.length === 0) return '';

  const tempState = editor.parseEditorState(
    JSON.stringify({
      root: {
        children: serializedNodes,
        direction: null,
        format: '',
        indent: 0,
        type: 'root',
        version: 1,
      },
    }),
  );

  let markdown = '';
  tempState.read(() => {
    markdown = $convertToMarkdownString(transformers, undefined, true);
  });
  return markdown;
}
